import { ComponentList } from '../../../components/core/component-list.js';
import { IEntity } from '../../../components/core/entity.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { isNotNullOrUndefined } from '../../../utility/null-safety.js';
import { VersionRange } from '../../core/version-range.js';
import { DateSpan } from '../date-span.js';
import { Fee } from '../fee.js';
import { LocalizedRichDescription } from '../localized-rich-description.js';
import { LocalizedString } from '../localized-string.js';
import { Price } from '../price.js';
import {
  cloneData, cloneDataOrNull, cloneList, DataModel, DataModelBuilder,
  isSameData, isSameList, SerializationContext, SerializedData,
} from '../../dto/dto.js';
import {
  fromEnumAsString, fromEnumAsStringOrNull, toEnumFromString, toEnumFromStringOrNull,
} from '../../../serialization/enum-utils.js';
import { Cardinal } from '../../core/cardinal.js';
import { LegacyOrderHook } from '../legacy-order-hook.js';
import { EntityMixin } from '../../../components/core/entity-mixin.js';
import { InvoiceVat } from '../invoicing/invoice-vat.js';
import { JSONUtils } from '../../../utility/json-utils.js';
import { OrderSideEffect } from '../orders/order-side-effect.js';
import { OrderSideEffectType } from '../orders/order-side-effect-type.js';
import { TicketAvailabilityModifier } from '../ticket-availability-modifier.js';
import { TicketTemplateChannelConfig, TicketTemplateChannelHelper } from '../../../utility/ticket-template-channel-helper.js';
import { TicketSpecialOffer } from './ticket-special-offer.js';
import { TicketTemplateChannel } from './ticket-template-channel.js';
import { TicketTemplateScope } from './ticket-template-scope.js';
import { TicketTemplateStatus } from './ticket-template-status.js';

export class TicketTemplate extends EntityMixin(DataModel) implements IEntity {
  static readonly TYPE_ID: string = '1633b79afe6f4b8295c29357d531e5ac';
  static readonly SCHEMA_VERSION: number = 18;

  public readonly channelConfig: TicketTemplateChannelConfig;

  private constructor(
    public readonly uniqueId: string,
    public readonly names: LocalizedString,
    public readonly richDescriptions: LocalizedRichDescription,
    public readonly dateSpan: DateSpan,
    public readonly scanDateSpan: DateSpan | null,

    // Selling price of the ticket.
    public readonly price: Price,

    // If there is an offer on the ticket this would be the original price before the offer was applied for the final
    // selling price. When the user applies a discount code, this is the selling price before the discount is applied.
    public readonly originalPrice: Price | null,

    // Fee taken by In Time on top of the ticket price.
    public readonly applicationFee: Fee | null,

    // Special offer applied to the ticket.
    public readonly specialOffer: TicketSpecialOffer | null,

    // Override VAT applied to the ticket.
    public readonly overrideVat: InvoiceVat | null,

    public readonly quantity: Cardinal,
    public readonly minQuantity: Cardinal,

    // If false, ticket is shown as unavailable. Can set on ticket card.
    private readonly _isPurchasable: boolean,

    // Whether the ticket has a limited quantity. Currently never set to true.
    public readonly isLimited: boolean,

    // If false, ticket is hidden on the website. Can set in ticket settings.
    private readonly _isPublic: boolean,

    // Whether the ticket can be purchased by the customer for themselves. If false, the ticket can only be gifted.
    private readonly _isPurchasableBySelf: boolean,

    public readonly status: TicketTemplateStatus,
    private readonly _scope: TicketTemplateScope,
    public readonly components: ComponentList,
    public readonly sideEffects: OrderSideEffect[],
    // When a ticket of this type is bought, all the sibling tickets are also incremented in the pool.
    public readonly siblingTicketTemplateIds: string[],
    // Users can only purchase supplements, together with a ticket.
    public readonly ticketSupplementTemplateId: string | null,
    public readonly availabilityModifier: TicketAvailabilityModifier | null,
    public readonly channel: TicketTemplateChannel | null,
    channelConfig?: TicketTemplateChannelConfig,
  ) {
    super();
    this.channelConfig = channelConfig ?? TicketTemplateChannelHelper.getConfig(this);
  }

  get $typeId(): string {
    return TicketTemplate.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(16, TicketTemplate.SCHEMA_VERSION);
  }

  get hasPositiveQuantity(): boolean {
    return this.quantity.isInfinite || this.quantity.value > 0;
  }

  get hasDiscount(): boolean {
    return isNotNullOrUndefined(this.originalPrice) && this.originalPrice.value > this.price.value;
  }

  get discountPercentage(): number {
    return isNotNullOrUndefined(this.originalPrice) ? 100.0 - (this.price.value * 100.0 / this.originalPrice.value) : 0;
  }

  /**
   * @deprecated Use channel instead.
   */
  get isPurchasableLegacy(): boolean {
    return this._isPurchasable;
  }

  get isPurchasable(): boolean {
    return this.channelConfig.isPurchasable ?? this._isPurchasable;
  }

  /**
   * @deprecated Use channel instead.
   */
  get isPublicLegacy(): boolean {
    return this._isPublic;
  }

  get isPublic(): boolean {
    return this.channelConfig.isPublic;
  }

  /**
   * @deprecated Use channel instead.
   */
  get isPurchasableBySelfLegacy(): boolean {
    return this._isPurchasableBySelf;
  }

  get isPurchasableBySelf(): boolean {
    return this.channelConfig.isPurchasableBySelf ?? this._isPurchasableBySelf;
  }

  /**
   * @deprecated Use channel instead.
   */
  get scopeLegacy(): TicketTemplateScope {
    return this._scope;
  }

  get scope(): TicketTemplateScope {
    return this.channelConfig.scope;
  }

  private static create(opts: {
    uniqueId: string,
    names: LocalizedString,
    richDescriptions: LocalizedRichDescription,
    dateSpan: DateSpan,
    scanDateSpan: DateSpan | null,
    price: Price,
    originalPrice: Price | null,
    applicationFee: Fee | null,
    specialOffer: TicketSpecialOffer | null,
    overrideVat: InvoiceVat | null,
    quantity: Cardinal,
    minQuantity: Cardinal,
    isPurchasable: boolean,
    isLimited: boolean,
    isPublic: boolean,
    isPurchasableBySelf: boolean,
    status: TicketTemplateStatus,
    scope: TicketTemplateScope,
    components: ComponentList,
    sideEffects: OrderSideEffect[],
    siblingTicketTemplateIds: string[],
    ticketSupplementTemplateId: string | null,
    availabilityModifier: TicketAvailabilityModifier | null,
    channel: TicketTemplateChannel | null,
    channelConfig?: TicketTemplateChannelConfig,
  }): TicketTemplate {
    return new TicketTemplate(
        opts.uniqueId,
        opts.names,
        opts.richDescriptions,
        opts.dateSpan,
        opts.scanDateSpan,
        opts.price,
        opts.originalPrice,
        opts.applicationFee,
        opts.specialOffer,
        opts.overrideVat,
        opts.quantity,
        opts.minQuantity,
        opts.isPurchasable,
        opts.isLimited,
        opts.isPublic,
        opts.isPurchasableBySelf,
        opts.status,
        opts.scope,
        opts.components,
        opts.sideEffects,
        opts.siblingTicketTemplateIds,
        opts.ticketSupplementTemplateId,
        opts.availabilityModifier,
        opts.channel,
        opts.channelConfig,
    );
  }

  static withoutChannel(opts: {
    uniqueId: string,
    names: LocalizedString,
    richDescriptions: LocalizedRichDescription,
    dateSpan: DateSpan,
    scanDateSpan: DateSpan | null,
    price: Price,
    originalPrice: Price | null,
    applicationFee: Fee | null,
    specialOffer: TicketSpecialOffer | null,
    overrideVat: InvoiceVat | null,
    quantity: Cardinal,
    minQuantity: Cardinal,
    isPurchasable: boolean,
    isLimited: boolean,
    isPublic: boolean,
    isPurchasableBySelf: boolean,
    status: TicketTemplateStatus,
    scope: TicketTemplateScope,
    components: ComponentList,
    sideEffects: OrderSideEffect[],
    siblingTicketTemplateIds: string[],
    ticketSupplementTemplateId: string | null,
    availabilityModifier: TicketAvailabilityModifier | null,
  }): TicketTemplate {
    const channelConfig = TicketTemplateChannelHelper.getConfig({
      isPurchasable: opts.isPurchasable,
      isPurchasableBySelf: opts.isPurchasableBySelf,
      isPublic: opts.isPublic,
      scope: opts.scope,
    });

    return this.create({
      ...opts,
      channel: null,
      channelConfig,
    });
  }

  static withChannel(opts: {
    uniqueId: string,
    names: LocalizedString,
    richDescriptions: LocalizedRichDescription,
    dateSpan: DateSpan,
    scanDateSpan: DateSpan | null,
    price: Price,
    originalPrice: Price | null,
    applicationFee: Fee | null,
    specialOffer: TicketSpecialOffer | null,
    overrideVat: InvoiceVat | null,
    quantity: Cardinal,
    minQuantity: Cardinal,
    isLimited: boolean,
    isPurchasableBySelf: boolean,
    status: TicketTemplateStatus,
    components: ComponentList,
    sideEffects: OrderSideEffect[],
    siblingTicketTemplateIds: string[],
    ticketSupplementTemplateId: string | null,
    availabilityModifier: TicketAvailabilityModifier | null,
    channel: TicketTemplateChannel,
  }): TicketTemplate {
    const channelConfig = TicketTemplateChannelHelper.getConfig(opts.channel);

    return this.create({
      ...opts,
      isPurchasable: channelConfig.isPurchasable ?? true,
      isPublic: channelConfig.isPublic,
      isPurchasableBySelf: channelConfig.isPurchasableBySelf ?? opts.isPurchasableBySelf,
      scope: channelConfig.scope,
      channelConfig,
    });
  }

  static fromMap(context: SerializationContext, map: SerializedData): TicketTemplate {
    return this.create({
      uniqueId: MapUtils.getString(map, 'uniqueId'),
      names: context.fromData<LocalizedString>(MapUtils.getRecord(map, 'names')),
      richDescriptions: context.fromData<LocalizedRichDescription>(MapUtils.getRecord(map, 'richDescriptions')),
      dateSpan: context.fromData<DateSpan>(MapUtils.getRecord(map, 'dateSpan')),
      scanDateSpan: context.fromDataOrNull<DateSpan>(MapUtils.getRecordOrNull(map, 'scanDateSpan')),
      price: Price.decode(MapUtils.getString(map, 'price')),
      originalPrice: Price.decodeOrNull(MapUtils.getStringOrNull(map, 'originalPrice')),
      applicationFee: context.fromDataOrNull<Fee>(MapUtils.getRecordOrNull(map, 'applicationFee')),
      specialOffer: toEnumFromStringOrNull(MapUtils.getStringOrNull(map, 'specialOffer'), TicketSpecialOffer),
      overrideVat: toEnumFromStringOrNull(MapUtils.getStringOrNull(map, 'overrideVat'), InvoiceVat),
      quantity: Cardinal.decode(MapUtils.getNumber(map, 'quantity')),
      minQuantity: Cardinal.decode(MapUtils.getNumber(map, 'minQuantity')),
      isPurchasable: MapUtils.getBoolean(map, 'isPurchasable'),
      isLimited: MapUtils.getBoolean(map, 'isLimited'),
      isPublic: MapUtils.getBoolean(map, 'isPublic'),
      isPurchasableBySelf: MapUtils.getBoolean(map, 'isPurchasableBySelf'),
      status: toEnumFromString(MapUtils.getString(map, '$status'), TicketTemplateStatus),
      scope: toEnumFromString(MapUtils.getString(map, 'scope'), TicketTemplateScope),
      components: ComponentList.deserialize(
          context, MapUtils.getList<SerializedData>(map, 'components', (data) => JSONUtils.decodeRecord(data)),
      ),
      sideEffects: MapUtils.getList<OrderSideEffect>(map, 'sideEffects', (value) => {
        return context.fromData<OrderSideEffect>(JSONUtils.decodeRecord(value));
      }),
      siblingTicketTemplateIds: MapUtils.getList<string>(map, 'siblingTicketTemplateIds', (value) => JSONUtils.decodeString(value)),
      ticketSupplementTemplateId: MapUtils.getStringOrNull(map, 'ticketSupplementTemplateId'),
      availabilityModifier: context.fromDataOrNull<TicketAvailabilityModifier>(MapUtils.getRecordOrNull(map, 'availabilityModifier')),
      channel: toEnumFromStringOrNull(MapUtils.getStringOrNull(map, 'channel'), TicketTemplateChannel),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'names': context.toData(this.names),
      'richDescriptions': context.toData(this.richDescriptions),
      'dateSpan': context.toData(this.dateSpan),
      'scanDateSpan': context.toDataOrNull(this.scanDateSpan),
      'price': this.price.encode(),
      'originalPrice': this.originalPrice?.encode() ?? null,
      'applicationFee': context.toDataOrNull(this.applicationFee),
      'specialOffer': fromEnumAsStringOrNull(this.specialOffer),
      'overrideVat': fromEnumAsStringOrNull(this.overrideVat),
      'quantity': this.quantity.encode(),
      'minQuantity': this.minQuantity.encode(),
      'isPurchasable': this._isPurchasable,
      'isLimited': this.isLimited,
      'isPublic': this._isPublic,
      'isPurchasableBySelf': this._isPurchasableBySelf,
      '$status': fromEnumAsString(this.status),
      'scope': fromEnumAsString(this._scope),
      'components': this.components.serialize(context),
      'sideEffects': this.sideEffects.map((e) => context.toData(e)),
      'siblingTicketTemplateIds': cloneList(this.siblingTicketTemplateIds),
      'ticketSupplementTemplateId': this.ticketSupplementTemplateId,
      'availabilityModifier': context.toDataOrNull(this.availabilityModifier),
      'channel': fromEnumAsStringOrNull(this.channel),
    };
  }

  clone(): this {
    return this.copyWith();
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
      isSameData(other.names, this.names) &&
      isSameData(other.richDescriptions, this.richDescriptions) &&
      isSameData(other.dateSpan, this.dateSpan) &&
      isSameData(other.scanDateSpan, this.scanDateSpan) &&
      isSameData(other.price, this.price) &&
      isSameData(other.originalPrice, this.originalPrice) &&
      isSameData(other.applicationFee, this.applicationFee) &&
      isSameData(other.quantity, this.quantity) &&
      isSameData(other.minQuantity, this.minQuantity) &&
      isSameList(other.sideEffects, this.sideEffects, isSameData) &&
      isSameList(other.siblingTicketTemplateIds, this.siblingTicketTemplateIds) &&
      other.specialOffer === this.specialOffer &&
      other.overrideVat === this.overrideVat &&
      other._isPurchasable === this._isPurchasable &&
      other.isLimited === this.isLimited &&
      other._isPublic === this._isPublic &&
      other._isPurchasableBySelf === this._isPurchasableBySelf &&
      other.status === this.status &&
      other._scope === this._scope &&
      isSameData(other.components, this.components) &&
      isSameData(other.availabilityModifier, this.availabilityModifier) &&
      other.channel === this.channel;
  }

  copyWith({
    uniqueId = this.uniqueId,
    names = cloneData(this.names),
    richDescriptions = cloneData(this.richDescriptions),
    dateSpan = cloneData(this.dateSpan),
    scanDateSpan = cloneDataOrNull(this.scanDateSpan),
    price = cloneData(this.price),
    originalPrice = cloneDataOrNull(this.originalPrice),
    applicationFee = cloneDataOrNull(this.applicationFee),
    specialOffer = this.specialOffer,
    overrideVat = this.overrideVat,
    quantity = cloneData(this.quantity),
    minQuantity = cloneData(this.minQuantity),
    isPurchasable = this._isPurchasable,
    isLimited = this.isLimited,
    isPublic = this._isPublic,
    isPurchasableBySelf = this._isPurchasableBySelf,
    status = this.status,
    scope = this._scope,
    components = cloneData(this.components),
    sideEffects = this.sideEffects.map((e) => cloneData(e)),
    siblingTicketTemplateIds = cloneList(this.siblingTicketTemplateIds),
    ticketSupplementTemplateId = this.ticketSupplementTemplateId,
    availabilityModifier = cloneDataOrNull(this.availabilityModifier),
    channel = this.channel,
  }: {
    uniqueId?: string,
    names?: LocalizedString,
    richDescriptions?: LocalizedRichDescription,
    dateSpan?: DateSpan,
    scanDateSpan?: DateSpan | null,
    price?: Price,
    originalPrice?: Price | null,
    applicationFee?: Fee | null,
    specialOffer?: TicketSpecialOffer | null,
    overrideVat?: InvoiceVat | null,
    quantity?: Cardinal,
    minQuantity?: Cardinal,
    isPurchasable?: boolean,
    isLimited?: boolean,
    isPublic?: boolean,
    isPurchasableBySelf?: boolean,
    status?: TicketTemplateStatus,
    scope?: TicketTemplateScope,
    components?: ComponentList,
    sideEffects?: OrderSideEffect[],
    siblingTicketTemplateIds?: string[],
    ticketSupplementTemplateId?: string | null,
    availabilityModifier?: TicketAvailabilityModifier | null,
    channel?: TicketTemplateChannel | null,
  } = {}): this {
    return TicketTemplate.create({
      uniqueId,
      names,
      richDescriptions,
      dateSpan,
      scanDateSpan,
      price,
      originalPrice,
      applicationFee,
      specialOffer,
      overrideVat,
      quantity,
      minQuantity,
      isPurchasable,
      isLimited,
      isPublic,
      isPurchasableBySelf,
      status,
      scope,
      components,
      sideEffects,
      siblingTicketTemplateIds,
      ticketSupplementTemplateId,
      availabilityModifier,
      channel,
    }) as this;
  }

  copyWithFee(applicationFee: Fee | null): TicketTemplate {
    return this.copyWith({ applicationFee });
  }

  removeSideEffectsWhere(test: (sideEffects: OrderSideEffect) => boolean): void {
    const newSideEffects: OrderSideEffect[] = [];

    for(const sideEffect of this.sideEffects) {
      if(!test(sideEffect)) {
        newSideEffects.push(sideEffect);
      }
    }

    this.sideEffects.length = 0;
    this.sideEffects.push(...newSideEffects);
  }

  clearSideEffects(): void {
    this.sideEffects.length = 0;
  }

  addOrReplaceSideEffect(sideEffect: OrderSideEffect): void {
    for(let i = 0; i < this.sideEffects.length; i++) {
      if(this.sideEffects[i].effectType === sideEffect.effectType) {
        this.sideEffects[i] = sideEffect;
        return;
      }
    }

    this.sideEffects.push(sideEffect);
  }

  findSideEffect(effectType: OrderSideEffectType): OrderSideEffect | null {
    for(let i = 0; i < this.sideEffects.length; i++) {
      if(this.sideEffects[i].effectType === effectType) {
        return this.sideEffects[i];
      }
    }

    return null;
  }

  hasSideEffect(effectType: OrderSideEffectType): boolean {
    return this.findSideEffect(effectType) != null;
  }
}

export class TicketTemplateBuilder extends DataModelBuilder<TicketTemplate> {
  build(context: SerializationContext, map: SerializedData): TicketTemplate {
    return TicketTemplate.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    this.migrateEnum(map, '$status', TicketTemplateStatus, TicketTemplateStatus.Live);
    this.migrateEnum(map, 'scope', TicketTemplateScope, TicketTemplateScope.Public);

    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(TicketTemplate.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(TicketTemplate.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version `fullPrice` of type `Price` was introduced. By default
    // it has the same value as `price`.
    if(version < 2) {
      map['fullPrice'] ??= map['price'];
      version = 2;
    }

    // In this version `status` of type `TicketTemplateStatus` was introduced.
    // Default value is `TicketTemplateStatus.Live`.
    if(version < 3) {
      map['$status'] ??= fromEnumAsString(TicketTemplateStatus.Live);
      version = 3;
    }

    // In this version `applicationFee` of type `Fee` and `specialOffer` of type `TicketSpecialOffer` was introduced.
    // Default value is `null`.
    if(version < 4) {
      map['applicationFee'] = null;
      map['specialOffer'] = null;
      version = 4;
    }

    // In this version `originalPrice` of type `Price | null` replaces the old `fullPrice` field.
    if(version < 5) {
      const price = Price.decode(MapUtils.get<string>(map, 'price'));
      const fullPrice = Price.decodeOrNull(MapUtils.getOrNull<string>(map, 'fullPrice'));

      if(isNotNullOrUndefined(fullPrice) && fullPrice.value > price.value) {
        map['originalPrice'] = fullPrice.encode();
      }
      else {
        map['originalPrice'] = null;
      }

      version = 5;
    }

    // In this version parameter `isLimited` of type `bool` was introduced.
    if(version < 6) {
      map['isLimited'] ??= false;
      version = 6;
    }

    // In this version parameter `isPublic` of type `bool` was introduced.
    if(version < 7) {
      map['isPublic'] ??= true;
      version = 7;
    }

    // In this version parameter `scope` of type `TicketTemplateScope` was introduced.
    if(version < 8) {
      map['scope'] ??= fromEnumAsString(TicketTemplateScope.Public);
      version = 8;
    }

    // In this version parameter `scanDateSpan` of type `Optional<DateSpan>` was
    // introduced. The default value is `null`.
    if(version < 9) {
      version = 9;
    }

    // In this version parameter `onOrderCompleteHooks` of type `OrderHook[]` was introduced.
    if(version < 10) {
      map['onOrderCompleteHooks'] = [];
      version = 10;
    }

    // In this version parameter `isPurchasableBySelf` of type `bool` was introduced.
    if(version < 11) {
      map['isPurchasableBySelf'] ??= true;
      version = 11;
    }

    // In this version parameter `overrideVat` of type `InvoiceVat | null` was introduced.
    if(version < 12) {
      version = 12;
    }

    // In this version parameter `siblingTicketTemplateIds` of type `string[]` was introduced.
    if(version < 13) {
      map['siblingTicketTemplateIds'] = [];
      version = 13;
    }

    // Version 14:
    // - add `ticketSupplementTemplateId` of type `string | null`.
    if(version < 14) {
      version = 14;
    }

    // Version 15:
    // - add `minQuantity` of type `Cardinal`.
    if(version < 15) {
      map['minQuantity'] = Cardinal.finite(1).encode();
      version = 15;
    }

    if(version < 16) {
      const onOrderCompleteHooks = MapUtils.getList<LegacyOrderHook>(map, 'onOrderCompleteHooks', (value) => {
        return context.fromData<LegacyOrderHook>(value as SerializedData);
      });

      const sideEffects = onOrderCompleteHooks.map((e) => e.toSideEffect()).filter(isNotNullOrUndefined);

      map['sideEffects'] = sideEffects.map((e) => context.toData(e));
      version = 16;
    }

    // Version 17:
    // - add `availabilityModifier` of type `TicketAvailabilityModifier | null`.
    if(version < 17) {
      version = 17;
    }

    // Version 18:
    // - add `channel` of type `TicketTemplateChannel | null`.
    if(version < 18) {
      version = 18;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
